import React from "react";
import get from "lodash/get";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import { AccessType, ResponsiveHeroImage } from "@quintype/components";
import { StoryHeaderCard } from "../story-page-components/story-header-card.js";
import { StoryTags } from "../story-page-components/story-tags.js";
import { RelatedStories } from "../story-page-components/related-stories.js";
import { PhotoStoryCard } from "../story-page-components/photo-story-card.js";
import { DfpAd } from "../dfp-ad";
import { MetypeCommentingWidget } from "@metype/components";
import { breakpoint } from "../../../utils/breakpoint";
import { addTargetBlankToExtLinks } from "../../../utils/add-target-blank-to-extlinks";
import { altTagOfHeroImages, getHorizontalSocialShare, getPrimaryColor, isEmpty } from "../../utils";
import { getAccessData, handleAccess, checkIfStoryPublic } from "../sso-components/sso-utils";

function getStoryCardTemplate(cards = [], story, config) {
  const cdnImage = get(config, ["cdn-image"], "");
  const socialShareStyle = getHorizontalSocialShare(config);
  const primaryColor = getPrimaryColor(config);

  return cards.map((card, index) => {
    return (
      <PhotoStoryCard
        key={card.id}
        card={card}
        story={story}
        cdn={cdnImage}
        primaryColor={primaryColor}
        socialShareStyle={socialShareStyle}
      />
    );
  });
}

function PhotoStoryTemplate(props) {
  const metypeConfig = get(props, ["config", "theme-attributes", "metype-config"], {});
  const storyAccess = get(props, ["access"]);
  const accessLoading = get(props, ["accessLoading"]);

  const imageElement = breakpoint("tablet") ? (
    <ResponsiveHeroImage
      story={props.story}
      aspectRatio={[16, 9]}
      alt={altTagOfHeroImages(props.story)}
      defaultWidth={480}
      widths={[250, 480, 640]}
      sizes="(max-width: 500px) 98%, (max-width: 768px) 48%, 98%"
      imgParams={{ auto: ["format", "compress"], fit: "max" }}
    />
  ) : (
    <ResponsiveHeroImage
      story={props.story}
      aspectRatio={[8, 3]}
      alt={altTagOfHeroImages(props.story)}
      defaultWidth={480}
      widths={[250, 480, 640, 960, 1200]}
      sizes="(max-width: 500px) 98%"
      imgParams={{ auto: ["format", "compress"], fit: "max" }}
    />
  );

  const isStoryPublic = checkIfStoryPublic(props.story);
  const storyCards = get(props, ["story", "cards"], []);

  return (
    <article className="blank-story">
      {props.story["hero-image-s3-key"] ? (
        <figure className="blank-story-hero-image qt-responsive-image">{imageElement}</figure>
      ) : (
        <div className="blank-story-hero-image__fallback-placeholder" />
      )}
      <div className="blank-story--wrapper">
        <div
          className={`blank-story--content ${
            get(props.config, ["theme-attributes", "language_direction"]) === "right" ? "ltr-text" : ""
          }`}
        >
          <StoryHeaderCard story={props.story} config={props.config} />

          {isStoryPublic
            ? getStoryCardTemplate(storyCards, props.story, props.config)
            : handleAccess(props.story, storyAccess, accessLoading, props.config, cards =>
                getStoryCardTemplate(cards, props.story, props.config)
              ) /* Method to check for access of the user */}

          <StoryTags tags={props.story.tags} config={props.config} />
          <div className="blank-story__aside">
            <div className="app-ad app-ad--story-mrec">
              <DfpAd adtype="Story-Mrec" />
            </div>
            <RelatedStories story={props.story} />
          </div>
        </div>
        {!props.preview && (
          <div>
            <div className="app-ad app-ad--story-horizontal">
              <DfpAd adtype="Story-Bottom-Ad" />
            </div>
            {!isEmpty(metypeConfig) && (
              <div className="blank-story--metype-widget">
                <MetypeCommentingWidget
                  host={metypeConfig.host}
                  accountId={parseInt(metypeConfig.accountId)}
                  pageURL={generateHostUrl(props.story)}
                  primaryColor={metypeConfig.primaryColor}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

PhotoStoryTemplate.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  preview: PropTypes.bool
};

function generateHostUrl(story = {}) {
  if (global.location) {
    return `${global.location.origin}/${story.slug}`;
  }
}

class PhotoStoryAccessWrapper extends React.Component {
  componentDidMount() {
    const { checkAccess, story, accessUpdated, accessIsLoading, config } = this.props;
    getAccessData(checkAccess, story, accessUpdated, accessIsLoading, config);
    addTargetBlankToExtLinks(this.storyWrapperElement);
  }
  render() {
    return (
      <div className="story-grid story-grid--photo" ref={el => (this.storyWrapperElement = el)}>
        <PhotoStoryTemplate {...this.props} />
      </div>
    );
  }
}

PhotoStoryAccessWrapper.propTypes = {
  checkAccess: PropTypes.func,
  story: PropTypes.object,
  accessUpdated: PropTypes.func,
  accessIsLoading: PropTypes.func,
  config: PropTypes.object
};

class PhotoStoryBase extends React.Component {
  render() {
    const email = get(this.props, ["member", "email"]);
    const phone = get(this.props, ["member", "metadata", "phone-number"], "1234");
    const accessTypeKey = get(this.props, ["config", "theme-attributes", "accesstypeKey"]);
    const disableMetering = get(this.props, ["config", "theme-attributes", "disableMetering"]) === "true";
    const isStaging = get(this.props, ["config", "theme-attributes", "accesstypeEnvironment"]) === "staging";
    const enableAccesstype = get(this.props, ["config", "theme-attributes", "enableAccesstype"]) === "true";

    return (
      <AccessType
        enableAccesstype={enableAccesstype}
        isStaging={isStaging}
        accessTypeKey={accessTypeKey} // "9rm1ijqQmGfJ2kwQ9zt2gWhK"
        email={email}
        phone={phone}
        disableMetering={disableMetering}
      >
        {({ initAccessType, checkAccess, accessUpdated, accessIsLoading }) => (
          <PhotoStoryAccessWrapper
            accessIsLoading={accessIsLoading}
            accessUpdated={accessUpdated}
            initAccessType={initAccessType}
            checkAccess={checkAccess}
            {...this.props}
          />
        )}
      </AccessType>
    );
  }
}

const mapStateToProps = state => ({
  member: get(state, ["member"], null),
  access: get(state, ["access"], null),
  accessLoading: get(state, ["accessLoading"], true)
});

export const PhotoStory = connect(mapStateToProps)(PhotoStoryBase);
